import { isEmpty } from "lodash"
import type { TFunction } from "i18next"

import { SECTION_TYPE } from "@/app/report/common/constants"
import { AxisY, ChartData, ChartRow, SectionGraph, Sort } from "@/app/report/common/constructor"
import {
  getDecimalPoint,
  getSummaryImplValue,
  parseSummary,
  parseUnit,
} from "@/app/report/common/tools"
import type { GraphSettings, MetricMapType, GraphOrientation } from "@/app/report/common/type"
import { CHART_TYPE } from "@/app/report/common/constants"
import { SORT_ORDER } from "@/constants/setting"
import type { MetricMeta } from "@/types/metric"
import type { PlayerSessionSummary, ReportTemplate } from "@/types/report"
import type { Unit } from "@/types/setting"

/**
 * Graph(Chart by Player) 섹션 — 리포트 화면(daily/page.tsx)과 PDF 빌더(BuilderD3Chart)가 함께 쓰는 단일 구현 (일감 #75).
 * 축 구성(지표·단위·소수 자릿수·색·차트 종류·수동 스케일)과 선수별 값 추출 규칙은 이 파일 밖에 두지 않는다.
 */

/** 템플릿 Graph 섹션 안의 차트 하나 — content 인덱스 + settings 배열 안의 인덱스 */
export type TemplateGraphChart = {
  sectionIndex: number
  chartIndex: number
  settings: GraphSettings
}

/** 리포트 템플릿의 Graph 섹션들에서 차트 설정을 모두 꺼낸다 (섹션 하나에 차트 여러 개) */
export const getTemplateGraphCharts = (template?: ReportTemplate | null): TemplateGraphChart[] =>
  (template?.content ?? []).flatMap((section, sectionIndex) =>
    section?.section === SECTION_TYPE.GRAPH && Array.isArray(section.settings)
      ? (section.settings as GraphSettings[]).map((settings, chartIndex) => ({
          sectionIndex,
          chartIndex,
          settings,
        }))
      : [],
  )

/** 아이템이 가리키는 차트. 없거나 템플릿에서 사라졌으면 첫 차트로 떨어진다 */
export const resolveTemplateGraphChart = (
  charts: TemplateGraphChart[],
  sectionIndex?: number,
  chartIndex?: number,
): TemplateGraphChart | null =>
  charts.find(
    (chart) => chart.sectionIndex === sectionIndex && chart.chartIndex === (chartIndex ?? 0),
  ) ??
  charts[0] ??
  null

/** 템플릿 정렬 설정 → Sort. 없으면 리포트 화면과 같은 기본(등번호 오름차순) */
/** 차트 방향 — 옛 템플릿·서버 응답에 없으면 가로(horizontal) (#185) */
export const resolveGraphOrientation = (
  settings: Pick<GraphSettings, "orientation"> | null | undefined,
): GraphOrientation => (settings?.orientation === "vertical" ? "vertical" : "horizontal")

/** 세로 차트의 X축 표시 — 없으면 보인다 (#185) */
export const resolveGraphShowAxis = (
  settings: Pick<GraphSettings, "showAxis"> | null | undefined,
): boolean => settings?.showAxis !== false

/** 세로 차트에서 허용하는 종류 — 막대·누적 막대만 (2026-09-30 결정). 그 외는 막대로 읽는다 */
export const VERTICAL_CHART_TYPES = [CHART_TYPE.BAR, CHART_TYPE.STACKED_BAR]
export const coerceVerticalChartType = (chartType: string): string =>
  VERTICAL_CHART_TYPES.includes(chartType) ? chartType : CHART_TYPE.BAR

export const resolveGraphSort = (settings: GraphSettings): Sort =>
  settings.sortSetting
    ? new Sort(settings.sortSetting.key, settings.sortSetting.order.toUpperCase() as "ASC" | "DESC")
    : new Sort("uniformNumber", SORT_ORDER.ASC)

/** 차트 설정 하나 + 선수 요약 → ChartData. 선수 요약은 API 원본이든 SummaryImpl 이든 받는다 */
export const buildChartData = ({
  settings: settingsItem,
  metricMap,
  selectedUnitList,
  players,
  t,
  translateKey,
}: {
  settings: GraphSettings
  metricMap: MetricMapType
  selectedUnitList: Unit[]
  players: PlayerSessionSummary | null | undefined
  t: TFunction | ((key: string) => string)
  translateKey: (key: string) => string | undefined
}): ChartData => {
  const sort = resolveGraphSort(settingsItem)
  const axisY: AxisY[] = settingsItem.yAxis.map((y) => {
    const metric = metricMap.get(y.metric)
    const unit = parseUnit(metric, selectedUnitList, metricMap) ?? null
    const decimalPoint = getDecimalPoint(metric?.key, unit)

    return new AxisY({
      name: translateKey(y.metric) || t(`metric.${y.metric}`),
      key: y.metric,
      unit,
      decimalPoint,
      color: y.color,
      chartType: y.chartType,
      flagShow: true,
      manualScaleActive: y.manualScaleActive,
      manualScaleValue: y.manualScaleValue,
    })
  })

  const dataChart: Array<ChartRow> = []
  players?.players?.forEach(({ summary, ...player }) => {
    const row = new ChartRow()
    const parsed = parseSummary(summary as Record<string, unknown>)

    row.name = `${player.backNumber} ${player.playerName}`
    row.header.set("backNumber", player.backNumber ?? null)
    row.header.set("uniformNumber", player.uniformNumber ?? player.backNumber ?? null)
    row.header.set("playerName", player.playerName ?? null)
    row.header.set("position", player.position ?? null)

    settingsItem.yAxis.forEach((y) => {
      const { value } = getSummaryImplValue(y.metric, parsed, metricMap, selectedUnitList)
      row.data.push(value)
    })

    dataChart.push(row)
  })

  return new ChartData(settingsItem, sort, axisY, dataChart)
}

/** 템플릿 Graph 섹션 설정(차트 여러 개) + 선수 요약 → SectionGraph (리포트 화면 createGraph 의 본체) */
export const buildGraphSection = ({
  graphSettings,
  metricList,
  selectedUnitList,
  metricMap,
  players,
  t,
  translateKey,
}: {
  graphSettings: GraphSettings[]
  metricList: MetricMeta[]
  selectedUnitList: Unit[]
  metricMap: MetricMapType
  players: PlayerSessionSummary | null | undefined
  t: TFunction | ((key: string) => string)
  translateKey: (key: string) => string | undefined
}): SectionGraph | null => {
  if (isEmpty(graphSettings) || isEmpty(metricList) || isEmpty(selectedUnitList)) {
    return null
  }
  const settings = structuredClone(graphSettings)
  const charts = settings.map((settingsItem) =>
    buildChartData({
      settings: settingsItem,
      metricMap,
      selectedUnitList,
      players,
      t,
      translateKey,
    }),
  )

  return new SectionGraph(SECTION_TYPE.GRAPH, charts)
}
